import { createSelector } from 'reselect'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

function formatPassengerSummary(numAdults: number, numChildren: number, numInfants: number): string {
  const intl = getCurrentIntl()
  const parts: Array<string> = []

  if (numAdults > 0) {
    parts.push(intl.formatMessage({
      id: 'DYZvrvG87g1VMJecUwRw',
      defaultMessage: '{count, plural, one {# Adult} other {# Adults}}',
      description: 'Charter upgrade passenger count label for adults in the checkout price breakdown',
    }, { count: numAdults }))
  }

  if (numChildren > 0) {
    parts.push(intl.formatMessage({
      id: 'BhMg1iglI3uJr64sGYXO',
      defaultMessage: '{count, plural, one {# Child} other {# Children}}',
      description: 'Charter upgrade passenger count label for children in the checkout price breakdown',
    }, { count: numChildren }))
  }

  if (numInfants > 0) {
    parts.push(intl.formatMessage({
      id: 'mPseBxFF1YkzjDMC8BMz',
      defaultMessage: '{count, plural, one {# Infant} other {# Infants}}',
      description: 'Charter upgrade passenger count label for infants in the checkout price breakdown',
    }, { count: numInfants }))
  }

  return parts.join(', ')
}

function getPassengerCounts(travellers: Array<App.FlightTraveller>) {
  let numAdults = 0
  let numChildren = 0
  let numInfants = 0

  for (const t of travellers) {
    const type = t.type?.toLowerCase?.() ?? 'adult'
    if (type === 'child' || type === 'chd' || type === 'cnn') {
      numChildren++
    } else if (type === 'infant' || type === 'inf' || type === 'ins') {
      numInfants++
    } else {
      numAdults++
    }
  }

  if (numAdults === 0 && numChildren === 0 && numInfants === 0) {
    numAdults = 1
  }

  return { numAdults, numChildren, numInfants }
}

function getSelectedTravellers(
  travellers: Array<App.FlightTraveller>,
  travellerIds?: Array<string>,
): Array<App.FlightTraveller> {
  if (!travellerIds) {
    return travellers.filter(traveller => traveller.type !== 'infant')
  }

  const selectedIds = new Set(travellerIds)
  return travellers.filter(traveller => selectedIds.has(traveller.id))
}

function getUniqueSelectedTravellerIds(legs: CharterUpgradeState['legs']): Array<string> {
  return [...new Set(legs.flatMap(leg => leg.travellerIds ?? []))]
}

interface CharterUpgradeState {
  legs: Array<App.CharterUpgradeLeg>
  searchId?: string
  flightItemId: string
}

function calculateUpgradePrice(
  upgradeOption: { price: App.UpgradeOptionPrice },
  passengerCounts: { numAdults: number; numChildren: number; numInfants: number },
): number {
  return upgradeOption.price.adult * passengerCounts.numAdults +
    upgradeOption.price.child * passengerCounts.numChildren +
    upgradeOption.price.infant * passengerCounts.numInfants
}

function getSeatDeduction(
  reservationAddons: App.FlightOrderItemDetails['reservationAddons'],
  upgradeFlightIds: Set<string>,
): number {
  return (reservationAddons ?? [])
    .filter(
      (a) =>
        a.addon_type === 'SEATS' &&
        a.status === 'confirmed' &&
        (a.flightId ? upgradeFlightIds.has(a.flightId) : true),
    )
    .reduce((sum, a) => sum + a.price, 0)
}

interface AvailableUpgrade {
  toCabinClass: string
  direction: string
  price: App.UpgradeOptionPrice
  flightIds: Array<string>
  seatsAvailable?: number
}

interface BuildCabinUpgradeAddonInputsParams {
  legs: CharterUpgradeState['legs']
  availableUpgrades: Array<AvailableUpgrade>
  searchId?: string
  travellers: Array<App.FlightTraveller>
}

export function findMatchingUpgrades(
  availableUpgrades: Array<AvailableUpgrade>,
  cabin: string,
  direction: string,
) {
  const match = availableUpgrades.find(
    u => u.toCabinClass === cabin && u.direction === direction,
  )
  return match ? [match] : []
}

function getSeatsAvailable(upgrade: AvailableUpgrade): number | undefined {
  return upgrade.seatsAvailable
}

function isSeatOccupyingTraveller(traveller: App.FlightTraveller): boolean {
  const type = traveller.type?.toLowerCase?.() ?? 'adult'
  return type !== 'infant' && type !== 'inf' && type !== 'ins'
}

function hasEnoughSeatsForTravellers(upgrade: AvailableUpgrade, travellers: Array<App.FlightTraveller>): boolean {
  const seatsAvailable = getSeatsAvailable(upgrade)
  const seatOccupyingTravellerCount = travellers.filter(isSeatOccupyingTraveller).length
  return typeof seatsAvailable === 'number' ? seatsAvailable >= seatOccupyingTravellerCount : true
}

export function filterAvailableUpgradesForTravellers<T extends AvailableUpgrade>(
  availableUpgrades: Array<T>,
  travellers: Array<App.FlightTraveller>,
): Array<T> {
  return availableUpgrades.filter(upgrade => hasEnoughSeatsForTravellers(upgrade, travellers))
}

export function buildCabinUpgradeAddonInputs({
  legs,
  availableUpgrades,
  searchId,
  travellers,
}: BuildCabinUpgradeAddonInputsParams): Array<App.CabinUpgradeAddonInput> {
  if (!searchId) {
    throw new Error('Cabin upgrade search ID is required')
  }

  const travellerIds = travellers
    .filter(traveller => traveller.type !== 'infant')
    .map(traveller => traveller.id)
    .filter(Boolean)
  if (travellerIds.length === 0) {
    throw new Error('Cabin upgrade traveller IDs are required')
  }

  return legs.flatMap(leg => {
    const legTravellerIds = leg.travellerIds ?? travellerIds
    if (legTravellerIds.length === 0) {
      throw new Error('Cabin upgrade traveller IDs are required')
    }

    const matchingUpgrades = findMatchingUpgrades(
      availableUpgrades,
      leg.cabin,
      leg.direction,
    )

    return matchingUpgrades.flatMap(upgrade =>
      upgrade.flightIds.map(flightId => ({
        searchId,
        type: 'CABIN_UPGRADE',
        payload: {
          toCabinClass: upgrade.toCabinClass,
          flightId,
          travellerIds: legTravellerIds,
        },
      })),
    )
  })
}

export function getCharterUpgradeTotalPrice(
  charterUpgrade: CharterUpgradeState,
  flightDetails: App.State['orders']['flightDetails'],
): number {
  const details = flightDetails[charterUpgrade.flightItemId]
  if (!details?.flight) return 0

  const flight = details.flight
  const availableUpgrades = flight.upgradeOptions?.availableUpgrades ?? []

  let upgradesTotal = 0
  const allUpgradeFlightIds = new Set<string>()
  const selectedTravellerIds = new Set(getUniqueSelectedTravellerIds(charterUpgrade.legs))

  for (const leg of charterUpgrade.legs) {
    const upgrades = findMatchingUpgrades(availableUpgrades, leg.cabin, leg.direction)
    const passengerCounts = getPassengerCounts(getSelectedTravellers(flight.travellers ?? [], leg.travellerIds))
    for (const u of upgrades) {
      upgradesTotal += calculateUpgradePrice(u, passengerCounts)
      u.flightIds.forEach((id) => allUpgradeFlightIds.add(id))
    }
  }

  if (!allUpgradeFlightIds.size) return 0

  const seatDeduction = getSeatDeduction(
    (flight.reservationAddons ?? []).filter(addon => !addon.travellerId || selectedTravellerIds.has(addon.travellerId)),
    allUpgradeFlightIds,
  )
  return Math.max(0, upgradesTotal - seatDeduction)
}

export const getCharterUpgradeBreakdownView = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.charterUpgrade,
  (state: App.State) => state.orders.flightDetails,
  (postPurchaseMode, charterUpgrade, flightDetails): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (postPurchaseMode !== 'charter-upgrade' || !charterUpgrade) {
      return { hasRequiredData: true, data: [] }
    }

    const details = flightDetails[charterUpgrade.flightItemId]
    if (!details?.flight) {
      return { hasRequiredData: false, data: [] }
    }

    const flight = details.flight
    const availableUpgrades = flight.upgradeOptions?.availableUpgrades ?? []

    let upgradesTotal = 0
    const allUpgradeFlightIds = new Set<string>()
    const selectedTravellerIds = new Set(getUniqueSelectedTravellerIds(charterUpgrade.legs))

    for (const leg of charterUpgrade.legs) {
      const upgrades = findMatchingUpgrades(availableUpgrades, leg.cabin, leg.direction)
      const passengerCounts = getPassengerCounts(getSelectedTravellers(flight.travellers ?? [], leg.travellerIds))

      for (const u of upgrades) {
        upgradesTotal += calculateUpgradePrice(u, passengerCounts)
        u.flightIds.forEach((id) => allUpgradeFlightIds.add(id))
      }
    }

    if (!allUpgradeFlightIds.size) {
      return { hasRequiredData: true, data: [] }
    }

    const seatDeduction = getSeatDeduction(
      (flight.reservationAddons ?? []).filter(addon => !addon.travellerId || selectedTravellerIds.has(addon.travellerId)),
      allUpgradeFlightIds,
    )

    const intl = getCurrentIntl()
    const journeyLegs = flight.bookingJourney.legs

    // Use the first leg's direction to determine the route text
    const primaryDirection = charterUpgrade.legs[0]?.direction
    const isMultiLegDifferentCabin = charterUpgrade.legs.length > 1
    let routeText = ''

    if (isMultiLegDifferentCabin) {
      const origin = journeyLegs[0]?.departureCity ?? ''
      const destination = journeyLegs[0]?.arrivalCity ?? ''

      routeText = origin && destination ? intl.formatMessage({
        id: 'IAnynthD61VcDoNvoV74',
        defaultMessage: 'Return {origin} to {destination}',
        description: 'Charter upgrade return route label shown in the checkout price breakdown',
      }, { origin, destination }) : ''
    } else {
      const legIndex = primaryDirection === 'return' ? 1 : 0
      const origin = journeyLegs[legIndex]?.departureCity ?? ''
      const destination = journeyLegs[legIndex]?.arrivalCity ?? ''

      routeText = origin && destination ? intl.formatMessage({
        id: 'PBvw5SRSJ_pdzRvZ4hwX',
        defaultMessage: 'One-way {origin} to {destination}',
        description: 'Charter upgrade one-way route label shown in the checkout price breakdown when only one leg is upgraded',
      }, { origin, destination }) : ''
    }

    const uniqueSelectedTravellerIds = getUniqueSelectedTravellerIds(charterUpgrade.legs)
    const passengerCounts = getPassengerCounts(getSelectedTravellers(flight.travellers ?? [], uniqueSelectedTravellerIds))
    const passengerSummary = formatPassengerSummary(passengerCounts.numAdults, passengerCounts.numChildren, passengerCounts.numInfants)

    const additionalInfo: Array<{ text: string }> = []
    if (routeText) additionalInfo.push({ text: routeText })
    if (passengerSummary) additionalInfo.push({ text: passengerSummary })

    const additionalElements: Array<App.Checkout.ItemBreakdownElement> = []
    if (seatDeduction > 0) {
      additionalElements.push({
        id: 'seat-deduction',
        type: 'price-line',
        left: {
          type: 'text',
          value: intl.formatMessage({
            id: '6bDRS5EyFpVOzS_mow6f',
            defaultMessage: 'Seat selection credit',
            description: 'Seat selection credit label shown in the charter upgrade checkout price breakdown',
          }),
        },
        right: { type: 'price', value: -seatDeduction },
      })
    }

    const items: Array<App.Checkout.CharterUpgradeBreakdownView> = [{
      itemType: 'charter-upgrade',
      title: '',
      additionalInfo,
      additionalElements,
    }]

    return {
      hasRequiredData: true,
      data: [{
        title: intl.formatMessage({
          id: 'XsIxMeeHcllZiON4Mudg',
          defaultMessage: 'Flight class upgrade',
          description: 'Charter upgrade section title in the checkout price breakdown',
        }),
        additionalInfoText: [],
        items,
        price: upgradesTotal,
        memberPrice: 0,
        ...(seatDeduction > 0 && { itemsAlwaysVisible: true, alwaysShowItemPrice: true }),
      }],
    }
  },
)
